import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ShoppingBag, Truck, Search, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReportPayload, PhotoItem } from '../../types'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../../config'
import { useBrandfetch } from '../../hooks/useBrandfetch'

// ─── Category inference from business type ────────────────────────────────────

function inferMenuCategories(category: string): string[] {
  const lower = category.toLowerCase()
  if (lower.includes('pizza')) return ['Pizzas', 'Sides', 'Drinks']
  if (lower.includes('burger') || lower.includes('fast food')) return ['Burgers', 'Fries & Sides', 'Drinks']
  if (lower.includes('taco') || lower.includes('mexican')) return ['Breakfast', 'Tacos', 'Burritos', 'Drinks']
  if (lower.includes('sushi') || lower.includes('japanese')) return ['Rolls', 'Nigiri', 'Sides', 'Drinks']
  if (lower.includes('coffee') || lower.includes('cafe')) return ['Coffee', 'Pastries', 'Breakfast']
  if (lower.includes('bakery')) return ['Breads', 'Pastries', 'Cakes', 'Drinks']
  if (lower.includes('chinese')) return ['Noodles', 'Rice Dishes', 'Dim Sum', 'Drinks']
  if (lower.includes('indian')) return ['Curry', 'Biryani', 'Breads', 'Drinks']
  if (lower.includes('grill') || lower.includes('bbq') || lower.includes('steakhouse')) return ['Starters', 'Mains', 'Sides', 'Drinks']
  if (lower.includes('salon') || lower.includes('spa') || lower.includes('beauty')) return ['Cuts & Styling', 'Color', 'Treatments']
  if (lower.includes('gym') || lower.includes('fitness')) return ['Memberships', 'Classes', 'Personal Training']
  return ['Featured', 'Popular', 'Drinks']
}

function inferItemNames(category: string): { name: string; desc: string }[] {
  const lower = category.toLowerCase()
  if (lower.includes('pizza')) return [
    { name: 'Margherita', desc: 'Tomato · Mozzarella · Fresh basil' },
    { name: 'Pepperoni', desc: 'Pepperoni · Mozzarella · Tomato sauce' },
    { name: 'BBQ Chicken', desc: 'Grilled chicken · BBQ sauce · Onion' },
  ]
  if (lower.includes('taco') || lower.includes('mexican')) return [
    { name: 'Classic Taco', desc: 'Seasoned meat · Onion · Cilantro' },
    { name: 'Loaded Burrito', desc: 'Rice · Beans · Cheese · Salsa' },
    { name: 'Quesadilla', desc: 'Cheese · Roasted peppers · Sour cream' },
  ]
  if (lower.includes('burger')) return [
    { name: 'Classic Smash', desc: 'Beef patty · Lettuce · Tomato · Pickles' },
    { name: 'Crispy Chicken', desc: 'Fried chicken · Coleslaw · House sauce' },
    { name: 'Double Stack', desc: 'Double beef · American cheese · Bacon' },
  ]
  if (lower.includes('coffee') || lower.includes('cafe')) return [
    { name: 'Signature Latte', desc: 'Double espresso · Steamed oat milk' },
    { name: 'Butter Croissant', desc: 'Flaky · Freshly baked · Golden' },
    { name: 'Avocado Toast', desc: 'Sourdough · Avocado · Poached egg' },
  ]
  return [
    { name: 'Signature Dish', desc: 'Chef\'s daily special — locally sourced' },
    { name: 'House Favourite', desc: 'Customer top-rated · Fresh ingredients' },
    { name: 'Chef\'s Special', desc: 'Curated selection · Best value' },
  ]
}

function categorisePhotos(photos: PhotoItem[]): {
  heroPhotos: string[]
  foodPhotos: string[]
} {
  const food: string[] = []
  const other: string[] = []
  for (const p of photos) {
    if (!p.url) continue
    const cat = (p.category ?? '').toUpperCase()
    if (cat === 'FOOD' || cat === 'MENU') {
      food.push(p.url)
    } else {
      other.push(p.url)
    }
  }
  const heroPhotos = [...other, ...food].slice(0, 6)
  const foodPhotos = food.length > 0 ? food.slice(0, 6) : [...other, ...food].slice(0, 6)
  return { heroPhotos, foodPhotos }
}

// ─── Phone UI sub-components ──────────────────────────────────────────────────

function PhoneStatusBar() {
  const now = new Date()
  const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  return (
    <div className="flex items-center justify-between px-5 pt-2 pb-1 flex-shrink-0">
      <span className="text-[11px] font-semibold text-gray-900">{time}</span>
      <div className="flex items-center gap-1">
        <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
          <rect x="0" y="7" width="3" height="4" rx="0.5" fill="#111827"/>
          <rect x="4.5" y="5" width="3" height="6" rx="0.5" fill="#111827"/>
          <rect x="9" y="2.5" width="3" height="8.5" rx="0.5" fill="#111827"/>
          <rect x="13.5" y="0" width="3" height="11" rx="0.5" fill="#111827" opacity="0.3"/>
        </svg>
        <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
          <path d="M7.5 8.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" fill="#111827"/>
          <path d="M4.5 6.5a4.5 4.5 0 0 1 6 0" stroke="#111827" strokeWidth="1.2" strokeLinecap="round"/>
          <path d="M1.5 3.5a9 9 0 0 1 12 0" stroke="#111827" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
        </svg>
        <svg width="24" height="12" viewBox="0 0 24 12" fill="none">
          <rect x="0.5" y="0.5" width="20" height="11" rx="3" stroke="#111827" strokeWidth="1"/>
          <rect x="2" y="2" width="14" height="8" rx="1.5" fill="#111827"/>
          <path d="M21.5 4v4a2 2 0 0 0 0-4z" fill="#111827" opacity="0.4"/>
        </svg>
      </div>
    </div>
  )
}

function AppHeader({
  logoUrl,
  name,
  brandColor,
}: {
  logoUrl: string | null
  name: string
  brandColor: string
}) {
  const { t } = useTranslation('report')
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-white flex-shrink-0">
      <div className="flex items-center gap-2">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={name}
            className="w-7 h-7 rounded object-contain bg-gray-50"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <div
            className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
            style={{ backgroundColor: brandColor }}
          >
            {name.charAt(0)}
          </div>
        )}
        <span className="text-xs font-semibold text-gray-900 max-w-[90px] truncate">{name}</span>
      </div>
      <div className="flex items-center gap-2">
        <button className="text-[10px] font-medium text-gray-700 border border-gray-200 px-2 py-0.5 rounded-md">
          {t('phone.signIn')}
        </button>
        <div className="w-5 h-5 flex flex-col justify-center gap-[3px] cursor-pointer">
          <span className="h-[1.5px] bg-gray-600 w-full rounded" />
          <span className="h-[1.5px] bg-gray-600 w-3/4 rounded" />
          <span className="h-[1.5px] bg-gray-600 w-full rounded" />
        </div>
      </div>
    </div>
  )
}

function HeroCarousel({ photos, brandColor }: { photos: string[]; brandColor: string }) {
  const { t } = useTranslation('report')
  const [idx, setIdx] = useState(0)
  const src = photos[idx] ?? ''

  return (
    <div className="relative w-full h-32 bg-gray-100 overflow-hidden flex-shrink-0">
      <AnimatePresence mode="wait">
        {src ? (
          <motion.img
            key={idx}
            src={src}
            alt="Business"
            className="w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <motion.div
            key="placeholder"
            className="w-full h-full flex items-center justify-center text-4xl"
            style={{ backgroundColor: `${brandColor}15` }}
          >
            🍽️
          </motion.div>
        )}
      </AnimatePresence>

      {photos.length > 1 && (
        <>
          <button
            onClick={() => setIdx(i => (i - 1 + photos.length) % photos.length)}
            className="absolute left-1.5 top-1/2 -translate-y-1/2 w-5 h-5 bg-white/80 rounded-full flex items-center justify-center text-[10px] shadow"
          >‹</button>
          <button
            onClick={() => setIdx(i => (i + 1) % photos.length)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 bg-white/80 rounded-full flex items-center justify-center text-[10px] shadow"
          >›</button>
        </>
      )}

      <div
        className="absolute bottom-2 right-2 text-[9px] font-bold text-white px-1.5 py-0.5 rounded"
        style={{ backgroundColor: BRAND.navy }}
      >
        {t('phone.poweredBy', { appName: APP_NAME })}
      </div>
    </div>
  )
}

function BusinessInfoBar({
  name,
  rating,
  logoUrl,
  brandColor,
}: {
  name: string
  rating: number
  logoUrl: string | null
  brandColor: string
}) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={name}
          className="w-9 h-9 rounded-full object-contain bg-gray-50 border-2 border-white shadow shrink-0"
          onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
        />
      ) : (
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold shrink-0 border-2 border-white shadow"
          style={{ backgroundColor: brandColor }}
        >
          {name.charAt(0)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-gray-900 truncate">{name}</p>
        <div className="flex items-center gap-1">
          <Star className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />
          <span className="text-[10px] text-gray-500">{rating.toFixed(1)}</span>
        </div>
      </div>
    </div>
  )
}

function OrderModeButtons({ brandColor }: { brandColor: string }) {
  const [mode, setMode] = useState<'pickup' | 'delivery'>('pickup')
  return (
    <div className="flex gap-2 px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
      {(['pickup', 'delivery'] as const).map(m => (
        <button
          key={m}
          onClick={() => setMode(m)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors"
          style={mode === m
            ? { backgroundColor: `${brandColor}15`, borderColor: brandColor, color: brandColor }
            : { backgroundColor: 'white', borderColor: '#e5e7eb', color: '#6b7280' }
          }
        >
          {m === 'pickup' ? <ShoppingBag className="w-2.5 h-2.5" /> : <Truck className="w-2.5 h-2.5" />}
          {m.charAt(0).toUpperCase() + m.slice(1)}
        </button>
      ))}
    </div>
  )
}

function CategoryTabs({ categories, brandColor }: { categories: string[]; brandColor: string }) {
  const [active, setActive] = useState(0)
  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 flex-shrink-0">
      <div className="flex gap-2 overflow-x-hidden">
        {categories.slice(0, 3).map((c, i) => (
          <button
            key={c}
            onClick={() => setActive(i)}
            className="text-[9px] font-semibold whitespace-nowrap px-2 py-0.5 rounded-full border transition-colors"
            style={active === i
              ? { backgroundColor: brandColor, borderColor: brandColor, color: 'white' }
              : { backgroundColor: 'white', borderColor: '#e5e7eb', color: '#6b7280' }
            }
          >
            {c}
          </button>
        ))}
      </div>
      <Search className="w-3 h-3 text-gray-400 shrink-0" />
    </div>
  )
}

function MenuItem({ item, photo }: { item: { name: string; desc: string }; photo?: string }) {
  return (
    <div className="flex items-start gap-2 py-2.5 border-b border-gray-50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-gray-900">{item.name}</p>
        <p className="text-[9px] text-gray-400 leading-relaxed">{item.desc}</p>
      </div>
      {photo && (
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 shrink-0">
          <img
            src={photo}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        </div>
      )}
    </div>
  )
}

function OrderCTAButton({ brandColor }: { brandColor: string }) {
  const { t } = useTranslation('report')
  return (
    <div className="px-4 py-2 border-t border-gray-100 bg-white flex-shrink-0">
      <button
        className="w-full py-2 rounded-xl text-white font-semibold text-[11px] shadow-sm"
        style={{ backgroundColor: brandColor }}
      >
        {t('phone.startOrdering')}
      </button>
    </div>
  )
}

function AppURLBar({ website }: { website?: string | null }) {
  const domain = website
    ? website.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '').split('/')[0]
    : 'yourbusiness.cartley.com'

  return (
    <div className="flex items-center justify-between px-3 py-1.5 border-t border-gray-200 bg-gray-50 rounded-b-[2px] flex-shrink-0">
      <span className="text-[8px] text-gray-400 truncate max-w-[120px]">{domain}</span>
      <div className="flex gap-2">
        {['↻', '⬛', '⋯'].map(ic => (
          <span key={ic} className="text-[9px] text-gray-400">{ic}</span>
        ))}
      </div>
    </div>
  )
}

// ─── Main PhoneMockup ─────────────────────────────────────────────────────────

interface PhoneMockupProps {
  report: ReportPayload
}

export function PhoneMockup({ report }: PhoneMockupProps) {
  const profile = report.profile
  const businessName = report.business_name

  const { logoUrl, primaryColor } = useBrandfetch(
    profile?.website,
    profile?.profile_photo_url,
  )

  const brandColor = primaryColor || BRAND.red
  const { heroPhotos, foodPhotos } = categorisePhotos(report.photos?.photos ?? [])
  const finalHeroPhotos = heroPhotos.length > 0 ? heroPhotos : [
    profile?.cover_photo_url,
    profile?.profile_photo_url,
  ].filter(Boolean) as string[]

  const category = profile?.category ?? (profile?.categories?.[0] ?? '')
  const rating = profile?.rating ?? 4.5
  const menuCategories = inferMenuCategories(category)
  const menuItems = inferItemNames(category)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="flex justify-center"
    >
      <div
        className="relative"
        style={{ width: 220, filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.25))' }}
      >
        <div
          className="relative overflow-hidden"
          style={{
            width: 220,
            borderRadius: 36,
            border: '8px solid #111',
            backgroundColor: '#111',
            boxShadow: 'inset 0 0 0 1px #333',
          }}
        >
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20" style={{ width: 72, height: 20, backgroundColor: '#111', borderRadius: 10 }} />
          <div className="bg-white flex flex-col overflow-hidden" style={{ borderRadius: 28, height: 460 }}>
            <PhoneStatusBar />
            <div className="flex-1 overflow-y-auto no-scrollbar">
              <AppHeader logoUrl={logoUrl} name={businessName} brandColor={brandColor} />
              <HeroCarousel photos={finalHeroPhotos} brandColor={brandColor} />
              <BusinessInfoBar name={businessName} rating={rating} logoUrl={logoUrl} brandColor={brandColor} />
              <OrderModeButtons brandColor={brandColor} />
              <CategoryTabs categories={menuCategories} brandColor={brandColor} />
              <div className="px-4 pt-2 pb-0.5">
                <p className="text-[11px] font-bold" style={{ color: brandColor }}>
                  {menuCategories[0]}
                </p>
              </div>
              <div className="px-4">
                {menuItems.map((item, i) => (
                  <MenuItem key={item.name} item={item} photo={foodPhotos[i] ?? finalHeroPhotos[i + 1]} />
                ))}
              </div>
            </div>
            <OrderCTAButton brandColor={brandColor} />
            <AppURLBar website={profile?.website} />
          </div>
        </div>
        <div className="absolute left-[-10px] top-20 w-1.5 h-8 bg-gray-700 rounded-l-sm" />
        <div className="absolute left-[-10px] top-32 w-1.5 h-12 bg-gray-700 rounded-l-sm" />
        <div className="absolute left-[-10px] top-48 w-1.5 h-12 bg-gray-700 rounded-l-sm" />
        <div className="absolute right-[-10px] top-28 w-1.5 h-16 bg-gray-700 rounded-r-sm" />
      </div>
    </motion.div>
  )
}

// ─── CartleyWebsitePreview ────────────────────────────────────────────────────

interface CartleyWebsitePreviewProps {
  report: ReportPayload
}

export function CartleyWebsitePreview({ report }: CartleyWebsitePreviewProps) {
  const { t } = useTranslation('report')

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="rounded-2xl overflow-hidden mb-8"
      style={{ background: `linear-gradient(135deg, ${BRAND.navy} 0%, #03224a 100%)` }}
    >
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* Left: copy */}
          <div className="flex-1 text-start">
            <div
              className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full mb-4 border"
              style={{ color: BRAND.red, borderColor: `${BRAND.red}50`, backgroundColor: `${BRAND.red}15` }}
            >
              {t('phone.powered', { appName: APP_NAME })}
            </div>

            <h3 className="text-xl font-bold text-white mb-3 leading-tight">
              {t('phone.seePower', { businessName: report.business_name })}
            </h3>
            <p className="text-sm text-gray-300 mb-5 leading-relaxed">
              {t('phone.desc')}
            </p>

            <div className="space-y-2.5 mb-6">
              {[
                t('phone.features.ordering'),
                t('phone.features.whatsapp'),
                t('phone.features.reviews'),
                t('phone.features.rankings'),
                t('phone.features.website'),
              ].map(item => (
                <p key={item} className="text-xs text-gray-200">{item}</p>
              ))}
            </div>

            <a
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 text-white font-semibold text-sm px-6 py-3 rounded-xl transition-colors shadow-lg max-sm:flex max-sm:w-full"
              style={{ backgroundColor: BRAND.red }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = BRAND.redHover)}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = BRAND.red)}
            >
              {t('phone.book')}
            </a>

            <p className="text-[10px] text-gray-400 mt-2.5">
              {t('phone.noCommitment')}
            </p>
          </div>

          {/* Right: phone mockup */}
          <div className="shrink-0">
            <PhoneMockup report={report} />
          </div>
        </div>
      </div>
    </motion.div>
  )
}
